/**
 * BelegungTile — 14-Tage-Bars + Big-Serif-Percent (Sprint-16 Mockup 4c).
 *
 * Alle 14 Bars gleich hoch — die Belegung kommt über Farbe, nicht Höhe.
 * Höhen-Varianz wäre falsches Signal (impliziert Preis/Menge). Sage für
 * belegte Tage, warm-tan (line/40) für freie. Bars-Container h-14 gibt der
 * Kachel visuelles Gewicht ohne den Serif-Percent zu erdrücken.
 */
import { CalendarDays } from 'lucide-react';
import type { CalendarDayCell } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { BentoTile } from './BentoTile';

interface BelegungTileProps {
  propertyId: string;
  cells: CalendarDayCell[] | undefined;
  stripFrom: string;
  occupancyPct: number | null;
  bookedDays: number;
}

const DAY_COUNT = 14;

export function BelegungTile({
  propertyId,
  cells,
  stripFrom,
  occupancyPct,
  bookedDays,
}: BelegungTileProps) {
  const isLoading = !cells;

  // Sicherstellen dass wir immer 14 Bars zeichnen, auch bei Loading/Partial-Data.
  const byDate = new Map<string, CalendarDayCell>();
  for (const c of cells ?? []) byDate.set(c.date, c);
  const days = Array.from({ length: DAY_COUNT }, (_, i) => {
    const d = new Date(stripFrom);
    d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });

  return (
    <BentoTile
      to="/properties/$id/preise"
      propertyId={propertyId}
      icon={<CalendarDays size={13} strokeWidth={2} />}
      eyebrow="Belegung · 14 Tage"
      cta="Preise & Belegung"
    >
      {/* Bars-Row */}
      <div className="mb-4 flex h-14 items-stretch gap-1" aria-hidden>
        {days.map((date, i) => {
          const cell = byDate.get(date);
          const isBooked = cell?.status === 'booked';
          return (
            <div
              key={i}
              className={cn(
                'flex-1 rounded-sm',
                isLoading ? 'bg-line/30 animate-pulse' : isBooked ? 'bg-sage' : 'bg-line/40',
              )}
            />
          );
        })}
      </div>

      {/* Big Percent + belegt */}
      <div className="flex items-baseline gap-2">
        <span className="text-ink font-serif text-4xl font-normal tabular-nums leading-none">
          {occupancyPct ?? '—'}
          <span className="text-ink-soft ml-1 text-xl">%</span>
        </span>
        <span className="text-ink-soft text-sm">belegt</span>
      </div>
      <p className="text-ink-faint mt-1 text-xs">
        {isLoading ? 'Wird geladen…' : `${bookedDays} von ${DAY_COUNT} Nächten`}
      </p>
    </BentoTile>
  );
}
